/**
 * useTaskSummaryState Hook
 *
 * Manages state for task AI summary component including loading, error, and display states.
 * Provides a clean interface for state management without mixing concerns.
 */

import { useState, useCallback } from "react";
import type { TaskSummaryData } from "@/lib/types/ai";

export interface TaskSummaryState {
  summary: TaskSummaryData | null;
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;
  displayedContent: string;
  isExpanded: boolean;
  generationStartTime: number | null;
  statusMessage: string;
}

export interface TaskSummaryStateActions {
  setSummary: (summary: TaskSummaryData | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  setDisplayedContent: (content: string) => void;
  setIsExpanded: (expanded: boolean) => void;
  setGenerationStartTime: (time: number | null) => void;
  setStatusMessage: (message: string) => void;
  reset: () => void;
  toggleExpanded: () => void;
}

const initialState: TaskSummaryState = {
  summary: null,
  isLoading: false,
  isStreaming: false,
  error: null,
  displayedContent: "",
  isExpanded: false,
  generationStartTime: null,
  statusMessage: "",
};

/**
 * Hook to manage task summary state
 */
export function useTaskSummaryState(): [TaskSummaryState, TaskSummaryStateActions] {
  const [state, setState] = useState<TaskSummaryState>(initialState);

  const setSummary = useCallback((summary: TaskSummaryData | null) => {
    setState((prev) => ({ ...prev, summary }));
  }, []);

  const setIsLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }));
  }, []);

  const setIsStreaming = useCallback((isStreaming: boolean) => {
    setState((prev) => ({ ...prev, isStreaming }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setDisplayedContent = useCallback((displayedContent: string) => {
    setState((prev) => ({ ...prev, displayedContent }));
  }, []);

  const setIsExpanded = useCallback((isExpanded: boolean) => {
    setState((prev) => ({ ...prev, isExpanded }));
  }, []);

  const setGenerationStartTime = useCallback((generationStartTime: number | null) => {
    setState((prev) => ({ ...prev, generationStartTime }));
  }, []);

  const setStatusMessage = useCallback((statusMessage: string) => {
    setState((prev) => ({ ...prev, statusMessage }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const toggleExpanded = useCallback(() => {
    setState((prev) => ({ ...prev, isExpanded: !prev.isExpanded }));
  }, []);

  const actions: TaskSummaryStateActions = {
    setSummary,
    setIsLoading,
    setIsStreaming,
    setError,
    setDisplayedContent,
    setIsExpanded,
    setGenerationStartTime,
    setStatusMessage,
    reset,
    toggleExpanded,
  };

  return [state, actions];
}
